<template>
  <BaseModal :modalTitle="$t('W: Opret prøveområde')">
    <UiBaseFormVee
      submitOnCtrlEnter
      :errorText="errorText"
      :onSubmit="onSubmit">
      <div class="mb-4 flex items-center gap-3">
        <MenuTriggerWrap
          menuName="IconPickerMenu"
          width="dynamic"
          :menuProps="{ value: form.icon, onSelect: onIconSelect }">
          <div
            class="flex h-10 w-10 items-center justify-center rounded-md border border-border-subtle text-text-secondary hover:bg-surface-sunken hover:text-text-primary">
            <DynamicLucideIcon v-if="form.icon" :name="form.icon" class="h-5 w-5" />
            <IconLucideImagePlus v-else class="h-5 w-5" />
          </div>
        </MenuTriggerWrap>
        <p class="text-sm text-text-muted">
          {{ $t('W: Vælg et ikon') }}
        </p>
      </div>

      <UiInputTextFieldVee
        name="name"
        :label="$t('term.name')"
        :placeholder="$t('form.placeholder.name')"
        :rules="['required']"
        isRequired
        :unit="currentLocale.toUpperCase()"
        v-model="form.name" />

      <UiInputSelectVee
        name="labReportTemplateID"
        :label="$t('W: Rapportskabelon')"
        :options="reportTemplateOptions"
        :rules="['required']"
        isRequired
        v-model="form.labReportTemplateID" />

      <RouterLink
        v-if="reportTemplateOptions.length === 0"
        :to="{ name: 'SettingsReportTemplates' }"
        class="mt-1 inline-block text-sm text-action hover:underline"
        @click="closeTop">
        {{ $t('W: Opret en rapportskabelon først') }}
      </RouterLink>

      <template #buttons>
        <UiBaseButton
          :text="$t('action.create')"
          :isLoading="isLoading"
          type="submit" />
      </template>
    </UiBaseFormVee>
  </BaseModal>
</template>

<script lang="ts">
import { mapActions, mapState } from 'pinia'
import { useToast } from '@/globals/typescript/utils/toast'
import { useSampleAreaStore } from '@/stores/sampleAreaStore'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useAppStore } from '@/stores/appStore'
import { useModalStore } from '@/stores/modalStore'
import { useUnsavedChangesGuard } from '@/composables/useUnsavedChangesGuard'

type TQuickCreateSampleAreaForm = {
  name: string
  icon: string | undefined
  labReportTemplateID: string
}

function createForm(): TQuickCreateSampleAreaForm {
  return {
    name: '',
    icon: undefined,
    labReportTemplateID: '',
  }
}

export default {
  name: 'QuickCreateSampleAreaModal',

  emits: ['close'],

  data() {
    return {
      unsavedGuard: useUnsavedChangesGuard(),
      form: createForm(),
      isLoading: false,
      errorText: '',
    }
  },

  computed: {
    ...mapState(useReportTemplateStore, ['allReportTemplates']),
    ...mapState(useAppStore, ['currentLocale']),

    reportTemplateOptions(): { text: string; value: string }[] {
      return [...this.allReportTemplates]
        .sort((a, b) =>
          a
            .getTitle(this.currentLocale)
            .localeCompare(b.getTitle(this.currentLocale))
        )
        .map((template) => ({
          text: template.getTitle(this.currentLocale),
          value: template.id,
        }))
    },
  },

  methods: {
    ...mapActions(useSampleAreaStore, ['createSampleArea']),
    ...mapActions(useModalStore, ['closeTop']),

    onIconSelect(iconName: string) {
      this.form.icon = iconName
    },

    async onSubmit() {
      this.isLoading = true
      this.errorText = ''

      try {
        const newSampleAreaID = await this.createSampleArea({
          name: this.form.name,
          icon: this.form.icon,
          labReportTemplateID: this.form.labReportTemplateID,
        })

        this.unsavedGuard.takeSnapshot()
        useToast().success(this.$t('W: Prøveområde oprettet'))
        this.$emit('close')
        this.$router.push({
          name: 'SampleArea',
          params: { sampleAreaID: newSampleAreaID },
        })
      } catch {
        this.errorText = this.$t('error.common.standard')
        useToast().error(this.$t('W: Kunne ikke oprette prøveområde'))
      } finally {
        this.isLoading = false
      }
    },
  },

  created() {
    // The modal is always the top entry at the moment it is created, so this
    // is its own ModalEntry id — it lets ModalHost tell "the modal I am about
    // to close is dirty" apart from "something underneath it is dirty".
    this.unsavedGuard.register({
      snapshot: () => ({ ...this.form }),
      modalId: useModalStore().top?.id,
    })
  },

  beforeUnmount() {
    this.unsavedGuard.unregister()
  },
}
</script>
